import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  Box,
  ButtonGroup,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import UploadFile from "components/_refactor/dropzone";
import ExistingLinkedFile from "./existing-linked-file";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  CONTENT_STATUS,
  ACCEPT_IMAGES,
  ACCEPT_VIDEOS,
  SEGMENTATION_OPTIONS,
  VIDEO_SOURCE_OPTIONS,
} from "../constants";
import {
  createContent,
  editContent,
  listCampaigns,
  listPlaylists,
  uploadMarketingFiles,
  buildSegmentacao,
  buildTags,
} from "../services/franchise-marketing-api";
import {
  populateContentForm,
  getAgendadoExtraFields,
} from "../utils/form-helpers";
import {
  resolveVideoSourceFromContent,
  getVideoEmbedUrl,
  isEmbeddableVideoUrl,
} from "../utils/video-helpers";

const EMPTY_FORM = {
  nome: "",
  url: "",
  categoria: null,
  campanha: null,
  playlist: null,
  descricao: "",
  duracao: "",
  status: null,
  segmentacao: null,
  tags: "",
  dataInicio: "",
  dataFim: "",
  dataPublicacaoAgendada: "",
};

export default function DialogVideo({
  open,
  onClose,
  onSaved,
  content,
  categoryOptions = [],
}) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [videoSource, setVideoSource] = useState("link");
  const [videoFiles, setVideoFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [campanhaOptions, setCampanhaOptions] = useState([]);
  const [playlistOptions, setPlaylistOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileResetBase = `${open}-${content?.IdFranquiaMarketingConteudo || content?.Id || "new"}`;

  const isLinkMode = videoSource === "link";
  const isUploadMode = videoSource === "upload";
  const existingUploadedFile = isUploadMode ? content?.UrlArquivo : null;

  const loadOptions = async () => {
    try {
      const [campaigns, playlists] = await Promise.all([
        listCampaigns(),
        listPlaylists(false),
      ]);
      setCampanhaOptions(
        campaigns.map((c) => ({
          value: c.IdFranquiaMarketingCampanha,
          label: c.Nome,
        })),
      );
      setPlaylistOptions(
        playlists.map((p) => ({
          value: p.IdFranquiaMarketingPlaylist,
          label: p.Nome,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (content) {
      const populated = populateContentForm(
        content,
        categoryOptions,
        campanhaOptions,
        playlistOptions,
      );
      if (populated) {
        setForm(populated);
        setVideoSource(resolveVideoSourceFromContent(content));
      }
    }
  }, [open, content, categoryOptions, campanhaOptions, playlistOptions]);

  useEffect(() => {
    if (!open || content) return;
    setForm(EMPTY_FORM);
    setVideoSource("link");
    setVideoFiles([]);
    setThumbnailFiles([]);
  }, [open, content]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSourceChange = (source) => {
    setVideoSource(source);
    if (source === "link") {
      setVideoFiles([]);
    } else {
      updateForm("url", "");
      updateForm("duracao", "");
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    updateForm("url", url);
    if (isEmbeddableVideoUrl(url)) {
      updateForm("duracao", "Detectada automaticamente");
    } else if (!url.trim()) {
      updateForm("duracao", "");
    }
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome do vídeo é obrigatório.", {
        severity: "error",
      });
      return;
    }

    if (!form.categoria?.value) {
      snackbar.showMessage("Categoria é obrigatória.", { severity: "error" });
      return;
    }

    if (isLinkMode && !form.url?.trim()) {
      snackbar.showMessage("Informe o link do vídeo (YouTube ou Vimeo).", {
        severity: "error",
      });
      return;
    }

    if (isUploadMode && !videoFiles.length && !existingUploadedFile) {
      snackbar.showMessage("Anexe o arquivo de vídeo.", { severity: "error" });
      return;
    }

    setSaving(true);

    try {
      const videoTitulo = form.nome.trim() || "video";
      const uploadFields = [
        { files: thumbnailFiles, fieldName: "thumbnail", titulo: videoTitulo },
      ];

      if (isUploadMode) {
        uploadFields.push({
          files: videoFiles,
          fieldName: "arquivo",
          titulo: `${videoTitulo}_file`,
        });
      }

      const filePayload = await uploadMarketingFiles(uploadFields);

      const payload = {
        titulo: form.nome.trim(),
        descricao: form.descricao,
        tipo: "Video",
        idFranquiaMarketingCategoria: form.categoria.value,
        idFranquiaMarketingCampanha: form.campanha?.value || null,
        idFranquiaMarketingPlaylist: form.playlist?.value || null,
        dataInicioVigencia: form.dataInicio || null,
        dataFimVigencia: form.dataFim || null,
        status: form.status?.value || "Rascunho",
        urlExterna: isLinkMode ? form.url.trim() : null,
        origemVideo: isLinkMode ? "Externo" : "Upload",
        tipoArquivo: isUploadMode ? "MP4" : null,
        tags: buildTags(form.tags),
        segmentacao: buildSegmentacao(form.segmentacao),
        ...getAgendadoExtraFields(form),
        ...filePayload,
      };

      const editId =
        content?.IdFranquiaMarketingConteudo || content?.id || content?.Id;

      if (editId) {
        await editContent({
          ...payload,
          idFranquiaMarketingConteudo: editId,
        });
        snackbar.showMessage("Vídeo atualizado com sucesso.", {
          severity: "success",
        });
      } else {
        await createContent(payload);
        snackbar.showMessage("Vídeo salvo com sucesso.", {
          severity: "success",
        });
      }

      onSaved?.();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(error?.message || "Falha ao salvar vídeo.", {
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const linkPreviewUrl = isLinkMode ? getVideoEmbedUrl(form.url) : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content ? "Editar Vídeo" : "Cadastro de Vídeo"}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome do vídeo"
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{
                    color: themeColorsDesingSystem.gray.dark,
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Origem do vídeo
                </Typography>
                <ButtonGroup fullWidth variant="outlined" color="primary">
                  {VIDEO_SOURCE_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      variant={
                        videoSource === option.value ? "contained" : "outlined"
                      }
                      color="primary"
                      onClick={() => handleSourceChange(option.value)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "1.5rem 1rem",
                      }}
                      startIcon={
                        option.value === "link" ? (
                          <LinkIcon />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
                <Typography
                  variant="caption"
                  style={{
                    color: themeColorsDesingSystem.gray.dark,
                    display: "block",
                    marginTop: 8,
                  }}
                >
                  {isLinkMode
                    ? "Use um link do YouTube ou Vimeo."
                    : "Envie um arquivo MP4, MOV ou AVI."}
                </Typography>
              </Grid>

              {isLinkMode && (
                <Grid item xs={12}>
                  <InputText
                    label="URL do vídeo (YouTube / Vimeo)"
                    value={form.url}
                    onChange={handleUrlChange}
                    placeholder="https://youtube.com/watch?v=..."
                    required
                  />
                  {linkPreviewUrl && isEmbeddableVideoUrl(form.url) && (
                    <Box marginTop={2}>
                      <Typography
                        variant="caption"
                        style={{ color: themeColorsDesingSystem.gray.dark }}
                      >
                        Pré-visualização
                      </Typography>
                      <Box
                        position="relative"
                        paddingTop="56.25%"
                        marginTop={1}
                      >
                        <iframe
                          title="Pré-visualização do vídeo"
                          src={linkPreviewUrl}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                            borderRadius: 8,
                          }}
                          allowFullScreen
                        />
                      </Box>
                    </Box>
                  )}
                  <ExistingLinkedFile
                    url={content?.UrlExterna}
                    hidden={!content?.UrlExterna || !isLinkMode}
                    variant="link"
                    label="Link atual vinculado"
                    openLabel="Abrir link atual"
                    helperText="Altere o campo acima apenas se quiser substituir o link."
                  />
                </Grid>
              )}

              {isUploadMode && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    style={{ color: themeColorsDesingSystem.gray.dark }}
                  >
                    Arquivo de vídeo
                  </Typography>
                  <UploadFile
                    message="Arraste o vídeo ou clique para selecionar"
                    accept={ACCEPT_VIDEOS}
                    onChangeFiles={setVideoFiles}
                    resetKey={`${fileResetBase}-video-${videoSource}`}
                  />
                  <ExistingLinkedFile
                    url={existingUploadedFile}
                    hidden={!!videoFiles.length}
                    label="Vídeo atual vinculado"
                    openLabel="Abrir vídeo atual"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={categoryOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Campanha vinculada"
                  value={form.campanha}
                  onChange={(v) => updateForm("campanha", v)}
                  options={campanhaOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Playlist"
                  value={form.playlist}
                  onChange={(v) => updateForm("playlist", v)}
                  options={playlistOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v)}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail personalizada
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setThumbnailFiles}
                  resetKey={`${fileResetBase}-thumbnail`}
                />
                <ExistingLinkedFile
                  url={content?.UrlThumbnail}
                  hidden={!!thumbnailFiles.length}
                  label="Thumbnail atual vinculada"
                  openLabel="Abrir thumbnail atual"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              {isLinkMode && (
                <Grid item xs={12}>
                  <InputText
                    label="Duração"
                    value={form.duracao}
                    disabled
                    placeholder="Detectada automaticamente"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InputText
                  label="Tags"
                  value={form.tags}
                  onChange={(e) => updateForm("tags", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              {form.status?.value === "Agendado" && (
                <Grid item xs={12}>
                  <InputText
                    label="Data/hora de publicação agendada"
                    type="datetime-local"
                    value={form.dataPublicacaoAgendada}
                    onChange={(e) =>
                      updateForm("dataPublicacaoAgendada", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          Salvar vídeo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
